import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Plus, Edit2, Trash2, Eye, EyeOff, DollarSign, Clock } from 'lucide-react';
import { services as servicesApi, addons as addonsApi } from '../utils/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const ServicesManagement = () => {
  const [activeTab, setActiveTab] = useState('services');
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [sizeCategories, setSizeCategories] = useState([]);
  const [sizingEnabled, setSizingEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);

  // Service form data
  const [serviceForm, setServiceForm] = useState({
    name: '',
    description: '',
    serviceCode: '',
    serviceType: 'full_groom',
    pricing: [
      { size: 'small', price: '', durationMinutes: '' },
      { size: 'medium', price: '', durationMinutes: '' },
      { size: 'large', price: '', durationMinutes: '' },
      { size: 'extra_large', price: '', durationMinutes: '' },
    ],
  });

  // Add-on form data
  const [addonForm, setAddonForm] = useState({
    name: '',
    description: '',
    price: '',
    displayOrder: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  // Reload data when tab becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadData();
      }
    };
    
    const handleFocus = () => {
      loadData();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Helper to create default pricing array from size categories
  const createDefaultPricing = () => {
    return sizeCategories
      .filter(cat => cat.is_active)
      .map(cat => ({
        size: cat.category_name,
        price: '',
        durationMinutes: ''
      }));
  };

  const loadData = async () => {
    try {
      const [servicesRes, addonsRes, sizeCategoriesRes, settingsRes] = await Promise.all([
        servicesApi.getAll({ includeInactive: true }),
        addonsApi.getAll({ includeInactive: true }),
        axios.get(`${API_URL}/size-categories`),
        axios.get(`${API_URL}/size-categories/settings`),
      ]);
      
      const servicesData = servicesRes.data;
      const categoriesData = sizeCategoriesRes.data;
      const sizingEnabledValue = settingsRes.data.sizing_enabled === 'true';
      
      // Check if any service has outdated size names
      if (sizingEnabledValue && categoriesData.length > 0 && servicesData.length > 0) {
        const activeCategoryNames = categoriesData
          .filter(cat => cat.is_active)
          .map(cat => cat.category_name);
        
        const needsMigration = servicesData.some(service => 
          service.pricing && service.pricing.some(p => 
            !activeCategoryNames.includes(p.size)
          )
        );
        
        if (needsMigration) {
          console.log('Detected outdated size names in services, running migration...');
          try {
            const token = sessionStorage.getItem('token');
            await axios.post(`${API_URL}/services/migrate-sizes`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Migration completed, reloading services...');
            
            // Reload services after migration
            const updatedServicesRes = await servicesApi.getAll({ includeInactive: true });
            setServices(updatedServicesRes.data);
          } catch (migrationError) {
            console.error('Migration failed:', migrationError);
            // Continue anyway, just show the data as-is
            setServices(servicesData);
          }
        } else {
          setServices(servicesData);
        }
      } else {
        setServices(servicesData);
      }
      
      setAddons(addonsRes.data);
      setSizeCategories(categoriesData);
      setSizingEnabled(sizingEnabledValue);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Service handlers
  const handleOpenServiceModal = (service = null) => {
    const defaultPricing = sizingEnabled ? createDefaultPricing() : [];
    
    if (service) {
      setEditingService(service);
      
      // When editing, sync pricing with current size categories
      let updatedPricing = defaultPricing;
      if (sizingEnabled && service.pricing) {
        // Merge existing pricing values with current size categories
        updatedPricing = defaultPricing.map(newPricing => {
          // Try to find matching pricing from the service
          const existingPricing = service.pricing.find(p => 
            p.size === newPricing.size || 
            p.size.startsWith(newPricing.size + '_') ||
            newPricing.size.startsWith(p.size + '_')
          );
          
          // If found, use existing values; otherwise use empty defaults
          return existingPricing 
            ? { ...newPricing, price: existingPricing.price, durationMinutes: existingPricing.durationMinutes }
            : newPricing;
        });
      }
      
      setServiceForm({
        name: service.name,
        description: service.description || '',
        serviceCode: service.service_code || '',
        serviceType: service.service_type,
        pricing: updatedPricing,
      });
    } else {
      setEditingService(null);
      setServiceForm({
        name: '',
        description: '',
        serviceCode: '',
        serviceType: 'full_groom',
        pricing: defaultPricing,
      });
    }
    setShowServiceModal(true);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await servicesApi.update(editingService.id, serviceForm);
        toast.success('Service updated successfully');
      } else {
        await servicesApi.create(serviceForm);
        toast.success('Service created successfully');
      }
      loadData();
      setShowServiceModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save service');
    }
  };

  const handleToggleService = async (serviceId) => {
    try {
      const service = services.find(s => s.id === serviceId);
      await servicesApi.update(serviceId, { isActive: !service.is_active });
      toast.success('Service status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update service status');
    }
  };

  // Add-on handlers
  const handleOpenAddonModal = (addon = null) => {
    if (addon) {
      setEditingAddon(addon);
      setAddonForm({
        name: addon.name,
        description: addon.description || '',
        price: addon.price,
        displayOrder: addon.display_order || 0,
      });
    } else {
      setEditingAddon(null);
      setAddonForm({
        name: '',
        description: '',
        price: '',
        displayOrder: 0,
      });
    }
    setShowAddonModal(true);
  };

  const handleAddonSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAddon) {
        await addonsApi.update(editingAddon.id, addonForm);
        toast.success('Add-on updated successfully');
      } else {
        await addonsApi.create(addonForm);
        toast.success('Add-on created successfully');
      }
      loadData();
      setShowAddonModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save add-on');
    }
  };

  const handleToggleAddon = async (addonId) => {
    try {
      await addonsApi.toggle(addonId);
      toast.success('Add-on status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update add-on status');
    }
  };

  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to deactivate this add-on?')) return;
    try {
      await addonsApi.delete(addonId);
      toast.success('Add-on deactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to deactivate add-on');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Services & Add-ons Management</h1>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('services')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'services'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Services
          </button>
          <button
            onClick={() => setActiveTab('addons')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'addons'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Add-ons
          </button>
        </div>

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Services</h2>
              <button
                onClick={() => handleOpenServiceModal()}
                className="btn btn-primary flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Add Service</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service) => (
                <div
                  key={service.id}
                  className={`card ${!service.is_active ? 'opacity-60' : ''}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{service.name}</h3>
                      <p className="text-sm text-gray-600 capitalize">
                        {service.service_type.replace('_', ' ')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleService(service.id)}
                      className={`p-2 rounded-lg ${
                        service.is_active
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {service.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                  </div>

                  {service.description && (
                    <p className="text-sm text-gray-600 mb-4">{service.description}</p>
                  )}

                  {sizingEnabled && service.pricing && service.pricing.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {service.pricing.map((p) => (
                        <div key={p.size} className="flex justify-between text-sm">
                          <span>{p.label || p.size}:</span>
                          <span className="font-semibold">
                            ${p.price} ({p.durationMinutes}min)
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => handleOpenServiceModal(service)}
                    className="w-full btn btn-secondary flex items-center justify-center space-x-2"
                  >
                    <Edit2 size={16} />
                    <span>Edit</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add-ons Tab */}
        {activeTab === 'addons' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Add-ons</h2>
              <button
                onClick={() => handleOpenAddonModal()}
                className="btn btn-primary flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Add Add-on</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {addons.map((addon) => (
                <div
                  key={addon.id}
                  className={`card ${!addon.is_active ? 'opacity-60' : ''}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold">{addon.name}</h3>
                      <p className="text-2xl font-bold text-primary-600 mt-2">
                        ${addon.price.toFixed(2)}
                      </p>
                    </div>
                    <button
                      onClick={() => handleToggleAddon(addon.id)}
                      className={`p-2 rounded-lg ${
                        addon.is_active
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {addon.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                    </button>
                  </div>

                  {addon.description && (
                    <p className="text-sm text-gray-600 mb-4">{addon.description}</p>
                  )}

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleOpenAddonModal(addon)}
                      className="flex-1 btn btn-secondary flex items-center justify-center space-x-2"
                    >
                      <Edit2 size={16} />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDeleteAddon(addon.id)}
                      className="flex-1 btn bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center space-x-2"
                    >
                      <Trash2 size={16} />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Service Modal */}
        {showServiceModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingService ? 'Edit Service' : 'Add New Service'}
              </h2>

              <form onSubmit={handleServiceSubmit} className="space-y-4">
                <div>
                  <label className="label">Service Name *</label>
                  <input
                    type="text"
                    required
                    className="input"
                    value={serviceForm.name}
                    onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    rows="3"
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  />
                </div>

                <div>
                  <label className="label">
                    Service code <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="input"
                    value={serviceForm.serviceCode}
                    onChange={(e) => setServiceForm({ ...serviceForm, serviceCode: e.target.value })}
                    placeholder="e.g. CPT-99213, GST-FULL, internal SKU"
                    maxLength={40}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Free-form code. Shown on invoices when the chosen template enables the
                    service-code column. Useful for insurance receipts (CPT, provincial codes)
                    or bookkeeping (internal SKUs).
                  </p>
                </div>

                <div>
                  <label className="label">Service Type *</label>
                  <select
                    required
                    className="input"
                    value={serviceForm.serviceType}
                    onChange={(e) => setServiceForm({ ...serviceForm, serviceType: e.target.value })}
                  >
                    <option value="full_groom">Full Groom</option>
                    <option value="bath_tidy">Bath & Tidy</option>
                    <option value="wash">Wash Only</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {sizingEnabled && (
                  <div>
                    <label className="label">Pricing by Size *</label>
                    <div className="space-y-3">
                      {serviceForm.pricing
                        .map((pricing, originalIndex) => {
                          const sizeCategory = sizeCategories.find(cat => cat.category_name === pricing.size);
                          return { pricing, originalIndex, sizeCategory };
                        })
                        .sort((a, b) => {
                          // Sort by display_order if both have categories
                          if (a.sizeCategory && b.sizeCategory) {
                            return (a.sizeCategory.display_order || 0) - (b.sizeCategory.display_order || 0);
                          }
                          // If one doesn't have a category, put it at the end
                          if (!a.sizeCategory) return 1;
                          if (!b.sizeCategory) return -1;
                          return 0;
                        })
                        .map(({ pricing, originalIndex, sizeCategory }) => {
                        return (
                          <div key={pricing.size} className="grid grid-cols-3 gap-3">
                            <div className="col-span-1">
                              <label className="text-sm text-gray-600">
                                {sizeCategory?.category_label || pricing.size}
                              </label>
                            </div>
                        <div>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                              type="number"
                              step="0.01"
                              required
                              placeholder="Price"
                              className="input pl-10"
                              value={pricing.price}
                              onChange={(e) => {
                                const newPricing = [...serviceForm.pricing];
                                newPricing[originalIndex].price = e.target.value;
                                setServiceForm({ ...serviceForm, pricing: newPricing });
                              }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="relative">
                            <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                              type="number"
                              required
                              placeholder="Minutes"
                              className="input pl-10"
                              value={pricing.durationMinutes}
                              onChange={(e) => {
                                const newPricing = [...serviceForm.pricing];
                                newPricing[originalIndex].durationMinutes = e.target.value;
                                setServiceForm({ ...serviceForm, pricing: newPricing });
                              }}
                            />
                          </div>
                        </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowServiceModal(false)}
                    className="flex-1 btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn btn-primary">
                    {editingService ? 'Update Service' : 'Create Service'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add-on Modal */}
        {showAddonModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingAddon ? 'Edit Add-on' : 'Add New Add-on'}
              </h2>

              <form onSubmit={handleAddonSubmit} className="space-y-4">
                <div>
                  <label className="label">Add-on Name *</label>
                  <input
                    type="text"
                    required
                    className="input"
                    value={addonForm.name}
                    onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    className="input"
                    rows="3"
                    value={addonForm.description}
                    onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                  />
                </div>

                <div>
                  <label className="label">Price *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="input pl-10"
                      value={addonForm.price}
                      onChange={(e) => setAddonForm({ ...addonForm, price: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="label">Display Order</label>
                  <input
                    type="number"
                    className="input"
                    value={addonForm.displayOrder}
                    onChange={(e) => setAddonForm({ ...addonForm, displayOrder: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddonModal(false)}
                    className="flex-1 btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn btn-primary">
                    {editingAddon ? 'Update Add-on' : 'Create Add-on'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesManagement;
